import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Canonical section order (matches backend section_runtime.PACKET_ROUTE)
// "Generational Patterns" is conditionally rendered — it only appears
// when the classifier detects generational signals. The progress UI
// lists the full canonical route; backend skip-events are already
// handled by the existing line parser.
const SECTIONS = [
  "Root Breach",
  "Satanic Strategy",
  "Manifestation Pressures",
  "False Agreements You May Have",
  "Strongholds in Place",
  "Generational Patterns",
  "Hidden Gifts Under Attack",
  "Before You Pray",
  "Prayer for You",
  "Jacob's Ladder Path for You",
  "What Resists You Here",
  "Fasting Recommendation",
  "Worship / Praise Support",
  "Summary",
];
const TOTAL_SECTIONS = SECTIONS.length;

const PHASE_FALLBACK = [
  "The Matrix is listening to your story…",
  "Discerning the root breach…",
  "Routing forgiveness where forgiveness is lawful…",
  "Compiling Jacob's Ladder for your path…",
  "Choosing the songs that meet you where you are…",
  "Sealing the packet under Christ…",
];

// Parse "[N/TOTAL] Section Name — repairing (1/2)…" → {current, section, repair}
function parsePhase(phase) {
  if (!phase) return { current: 0, section: null, repair: null, sealing: false, done: false };
  if (/^done\.?$/i.test(phase.trim())) return { current: TOTAL_SECTIONS, section: null, repair: null, sealing: false, done: true };
  if (/sealing|seal(ing)?/i.test(phase)) return { current: TOTAL_SECTIONS, section: null, repair: null, sealing: true, done: false };
  const m = phase.match(/^\[(\d+)\/\d+\]\s*(.+?)(?:\s*—\s*repairing\s*\((\d+)\/(\d+)\))?…?$/i);
  if (m) {
    return {
      current: parseInt(m[1], 10),
      section: m[2].replace(/…$/, "").trim(),
      repair: m[3] ? { pass: parseInt(m[3], 10), of: parseInt(m[4], 10) } : null,
      sealing: false,
      done: false,
    };
  }
  return { current: 0, section: null, repair: null, sealing: false, done: false };
}

export default function Generating() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const intake = location.state?.intake;
  const [phase, setPhase] = useState("Preparing the Matrix…");
  const [err, setErr] = useState("");
  const launched = useRef(false);
  const phaseIdx = useRef(0);
  const phaseTimer = useRef(null);
  const hasStructured = useRef(false);

  // Rotate ambient phase text ONLY until the first structured phase arrives
  useEffect(() => {
    phaseTimer.current = setInterval(() => {
      if (hasStructured.current) return;
      phaseIdx.current = (phaseIdx.current + 1) % PHASE_FALLBACK.length;
      setPhase(PHASE_FALLBACK[phaseIdx.current]);
    }, 4500);
    return () => phaseTimer.current && clearInterval(phaseTimer.current);
  }, []);

  const [readyPacketId, setReadyPacketId] = useState(null);
  const jobIdRef = useRef(null);

  useEffect(() => {
    // ──────── Tab-restore recovery guard (Android Chrome) ────────
    // If the tab was killed and restored, intake state will be gone. Check
    // sessionStorage for: (1) a ready packet (go straight there) or (2) an
    // active job ID (resume polling without starting a new compile).
    let immediatePollRequested = false;
    let wakeLock = null;
    let onVisibilityChange = null;
    const ctrl = new AbortController();

    const requestWakeLock = async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLock = await navigator.wakeLock.request("screen");
        }
      } catch { /* ignore */ }
    };

    const waitWithWake = (ms) =>
      new Promise((resolve) => {
        const start = Date.now();
        const check = () => {
          if (ctrl.signal.aborted) return resolve();
          if (immediatePollRequested) {
            immediatePollRequested = false;
            return resolve();
          }
          if (Date.now() - start >= ms) return resolve();
          setTimeout(check, 250);
        };
        check();
      });

    const pollJob = async (jobId) => {
      const MAX_POLLS = 180; // ~6 minutes at 2s
      for (let i = 0; i < MAX_POLLS; i++) {
        await waitWithWake(2000);
        if (ctrl.signal.aborted) return;
        try {
          const r = await fetch(`${API}/packets/job/${jobId}`);
          if (!r.ok) continue;
          const j = await r.json();
          if (j.phase) {
            hasStructured.current = true;
            setPhase(j.phase);
          }
          if (j.status === "ready") {
            try {
              sessionStorage.setItem("rm_last_ready_packet", j.packet_id);
              sessionStorage.removeItem("rm_active_job_id");
            } catch { /* ignore */ }
            setReadyPacketId(j.packet_id);
            const target = `/packet/${j.packet_id}`;
            try { navigate(target, { replace: true }); } catch { /* ignore */ }
            // Belt-and-suspenders: if for any reason react-router did not
            // unmount this page within 1.2s (e.g. throttled timer, error
            // boundary, navigation-cancelled by an in-flight effect),
            // force a full-page navigation so the user always lands on
            // the packet.
            setTimeout(() => {
              if (window.location.pathname !== target) {
                window.location.assign(target);
              }
            }, 1200);
            return;
          }
          if (j.status === "failed") {
            try { sessionStorage.removeItem("rm_active_job_id"); } catch { /* ignore */ }
            setErr(j.error || "Generation failed. Please try again.");
            return;
          }
        } catch {
          /* transient network — keep polling */
        }
      }
      setErr("Generation is taking longer than expected. Please try again.");
    };

    const startNewJob = async () => {
      try {
        const startRes = await fetch(`${API}/packets/start-cloud-job`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(intake),
        });
        if (!startRes.ok) {
          // Feb 2026: when the backend rejects a too-thin intake with
          // a pastoral 400 message in `detail`, surface that message
          // directly to the user instead of a generic error.
          let detail = "";
          try {
            const errBody = await startRes.json();
            detail = errBody?.detail || "";
          } catch { /* not JSON — fall back to status */ }
          if (startRes.status === 400 && detail) {
            throw new Error(detail);
          }
          throw new Error(detail || `Generation could not start (${startRes.status}). Please reload and try again.`);
        }
        const startData = await startRes.json();
        const jobId = startData.job_id;
        jobIdRef.current = jobId;
        // CRITICAL: put the job_id in the URL itself so an Android tab-kill
        // + restore always lands back here with the same job to resume.
        setSearchParams({ job: jobId }, { replace: true });
        try { sessionStorage.setItem("rm_active_job_id", jobId); } catch { /* ignore */ }
        await pollJob(jobId);
      } catch (e) {
        setErr(e.message || "Could not start generation.");
      }
    };

    const run = async () => {
      // FIRST PRIORITY: URL query param ?job=xxx — this survives tab kills
      // on Android Chrome where sessionStorage sometimes does not.
      const urlJob = searchParams.get("job");
      if (urlJob) {
        jobIdRef.current = urlJob;
        try { sessionStorage.setItem("rm_active_job_id", urlJob); } catch { /* ignore */ }
        await pollJob(urlJob);
        return;
      }

      // Second: cached completed packet (straight to the result)
      try {
        const cachedPacket = sessionStorage.getItem("rm_last_ready_packet");
        if (cachedPacket) {
          sessionStorage.removeItem("rm_last_ready_packet");
          navigate(`/packet/${cachedPacket}`, { replace: true });
          return;
        }
        // Third: session-stored active job (tab-kill recovery)
        const resumeJob = sessionStorage.getItem("rm_active_job_id");
        if (resumeJob) {
          jobIdRef.current = resumeJob;
          // Also put it in the URL so future reloads survive too
          setSearchParams({ job: resumeJob }, { replace: true });
          await pollJob(resumeJob);
          return;
        }
      } catch { /* ignore */ }

      // Otherwise we need an intake to kick off a new job
      if (!intake) {
        navigate("/intake", { replace: true });
        return;
      }
      if (launched.current) return;
      launched.current = true;
      await startNewJob();
    };

    // Wake lock + visibility listener apply in all active paths
    requestWakeLock();
    onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestWakeLock();
        immediatePollRequested = true;
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    run();

    return () => {
      ctrl.abort();
      if (onVisibilityChange) document.removeEventListener("visibilitychange", onVisibilityChange);
      if (wakeLock) {
        try { wakeLock.release(); } catch { /* ignore */ }
        wakeLock = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parsed = parsePhase(phase);
  // Progress %:
  //   done       → 100
  //   sealing    → static 95% (NO fake creep — Android throttles timers
  //                and "97% that never finishes" is the #1 support bug)
  //   section N  → up to 92% (sections split across 92%)
  const [stuckSeconds, setStuckSeconds] = useState(0);

  // "Stuck" detector — once we're in sealing phase or at the final section,
  // tick up a seconds counter. After 3s, surface a big tap-to-continue
  // button — Android commonly throttles our poll loop in under 10s.
  const isStuckCandidate = parsed.sealing || parsed.current >= TOTAL_SECTIONS;
  useEffect(() => {
    if (!isStuckCandidate) {
      setStuckSeconds(0);
      return;
    }
    const id = setInterval(() => setStuckSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [isStuckCandidate]);

  // Manual poke: force a fresh poll of the job status. Used by the
  // "Tap to open" button when Android timers are throttled.
  const pokeJob = async () => {
    const jid = jobIdRef.current;
    if (!jid) return;
    try {
      const r = await fetch(`${API}/packets/job/${jid}`);
      if (!r.ok) return;
      const j = await r.json();
      if (j.phase) { hasStructured.current = true; setPhase(j.phase); }
      if (j.status === "ready") {
        try { sessionStorage.removeItem("rm_active_job_id"); } catch { /* ignore */ }
        setReadyPacketId(j.packet_id);
        const target = `/packet/${j.packet_id}`;
        try { navigate(target, { replace: true }); } catch { /* ignore */ }
        setTimeout(() => {
          if (window.location.pathname !== target) {
            window.location.assign(target);
          }
        }, 1200);
      } else if (j.status === "failed") {
        setErr(j.error || "Generation failed.");
      }
    } catch {
      /* ignore — the user can tap again */
    }
  };

  const pct = parsed.done
    ? 100
    : parsed.sealing
      ? 95 // honest, static — never creeps
      : Math.round(((parsed.current || 0) / TOTAL_SECTIONS) * 92);

  // Monotonic progress — never regress. Phases like provider-switch
  // messages or any unstructured text would otherwise collapse the bar
  // to 0%. We only ever move forward, and only snap to 100% on done.
  const [displayPct, setDisplayPct] = useState(0);
  // Monotonic "section dots" — same rationale: don't let the dot ladder
  // flash dim when an intermediate non-structured phase lands.
  const [displayCurrent, setDisplayCurrent] = useState(0);
  const [displaySealing, setDisplaySealing] = useState(false);
  const [displayDone, setDisplayDone] = useState(false);
  useEffect(() => {
    setDisplayPct((prev) => {
      if (parsed.done) return 100;
      // If parser can't anchor the phase, keep the last known value.
      if (!parsed.sealing && !parsed.current) return prev;
      return Math.max(prev, pct);
    });
    setDisplayCurrent((prev) =>
      parsed.current && parsed.current > prev ? parsed.current : prev
    );
    if (parsed.sealing) setDisplaySealing(true);
    if (parsed.done) setDisplayDone(true);
  }, [pct, parsed.done, parsed.sealing, parsed.current]);

  // Auto-rescue: once we've been in sealing/final-section state for >=4s
  // OR the bar has reached 92%+, start firing pokeJob() every 3s on our
  // own. The user shouldn't have to know about Android timer throttling.
  // Mobile Edge / Chrome can freeze setInterval to once-per-minute on a
  // backgrounded tab, so we lean on (a) periodic pokes, (b) a
  // visibilitychange listener, and (c) the visible rescue button.
  useEffect(() => {
    const inHomeStretch = stuckSeconds >= 4 || displayPct >= 92;
    if (!inHomeStretch || readyPacketId || err) return;
    pokeJob();
    const id = setInterval(pokeJob, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stuckSeconds >= 4, displayPct >= 92, readyPacketId, err]);

  // Whenever the tab becomes visible again (user returns from background),
  // immediately poke. Mobile browsers freeze setInterval on backgrounded
  // tabs but DO fire visibilitychange the moment the tab returns to
  // foreground — so this single listener catches the "stuck because timer
  // was frozen" case the moment the user looks again.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && !readyPacketId && !err) {
        pokeJob();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyPacketId, err]);

  return (
    <main className="relative min-h-screen overflow-hidden" data-testid="generating-page">
      <motion.div
        className="fixed inset-0 z-0 pointer-events-none flex items-center justify-center"
        aria-hidden="true"
        animate={{ rotate: 360 }}
        transition={{ duration: 180, ease: "linear", repeat: Infinity }}
      >
        <div
          className="rm-pulse-glow"
          style={{
            width: "min(140vh, 140vw)",
            height: "min(140vh, 140vw)",
            backgroundImage: "url('/visuals/art_mandala.png')",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            opacity: 0.45,
          }}
        />
      </motion.div>
      <div className="fixed inset-0 z-[1] pointer-events-none" aria-hidden="true"
           style={{
             background:
               "radial-gradient(ellipse at center, rgba(5,8,24,0.45) 0%, rgba(5,8,24,0.82) 60%, rgba(5,8,24,0.96) 100%)",
           }} />

      {!err && (
        <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.6 }}
            className="text-center w-full max-w-3xl"
          >
            <div className="overline mb-10" data-testid="generating-overline">Compiling</div>

            {/* Breathing focal circle */}
            <motion.div
              animate={{ scale: [1, 1.06, 1], opacity: [0.55, 1, 0.55] }}
              transition={{ duration: 5, ease: "easeInOut", repeat: Infinity }}
              className="w-24 h-24 mx-auto mb-12 rounded-full border border-[#FFE7A0]/55"
              style={{ boxShadow: "0 0 80px rgba(255, 231, 160,0.35), inset 0 0 40px rgba(255, 231, 160,0.15)" }}
            />

            {/* Section counter — uses MONOTONIC display state so the
                phase never appears to regress. Once displayPct hits 92%+,
                we treat the page as in the home stretch regardless of
                whether the most recent poll caught the explicit "Sealing"
                phase (mobile timer throttling can skip it). */}
            <div
              className="font-cinzel text-[0.7rem] tracking-[0.3em] uppercase text-[#FFE7A0]/90 mb-4"
              data-testid="generating-counter"
              style={{ textShadow: "0 0 20px rgba(5,8,24,0.95)" }}
            >
              {displayDone
                ? `Complete · ${TOTAL_SECTIONS} of ${TOTAL_SECTIONS}`
                : displaySealing || displayPct >= 92
                  ? "Sealing under Christ…"
                  : displayCurrent > 0
                    ? `Section ${displayCurrent} of ${TOTAL_SECTIONS}`
                    : "Preparing the field"}
            </div>

            {/* Current section name — also uses monotonic state. When the
                page is in the sealing/almost-there zone, override any
                stale section title with a sealing message so the user
                doesn't see "Section 4 of 13: Hidden Gifts Under Attack"
                while the bar reads 95%. */}
            <h1
              className="font-serif-display italic text-3xl sm:text-4xl lg:text-5xl text-[#F0E6D2] leading-tight mb-3"
              data-testid="generating-title"
              style={{ textShadow: "0 2px 50px rgba(5,8,24,0.95)" }}
            >
              {displayDone
                ? "Ready"
                : displaySealing || displayPct >= 92
                  ? "Sealing the packet under Christ"
                  : parsed.section || "The matrix is restoring…"}
            </h1>

            {/* Repair note (only when audit→repair is active) */}
            <AnimatePresence mode="wait">
              {parsed.repair && (
                <motion.p
                  key={`repair-${parsed.current}-${parsed.repair.pass}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-[#FFE7A0]/70 italic font-body font-light text-sm mb-5"
                  data-testid="generating-repair"
                >
                  Refining this section · pass {parsed.repair.pass} of {parsed.repair.of}
                </motion.p>
              )}
            </AnimatePresence>

            {/* Ambient phrase rotator (fallback before structured phases arrive) */}
            <AnimatePresence mode="wait">
              {!parsed.section && !parsed.sealing && !parsed.done && (
                <motion.p
                  key={phase}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.9 }}
                  className="text-[#F0E6D2]/70 italic font-body font-light text-base sm:text-lg mb-10"
                  data-testid="generating-invocation"
                >
                  {phase}
                </motion.p>
              )}
            </AnimatePresence>

            {/* Percentage bar */}
            <div className="mt-10 mb-6 relative h-[2px] w-full max-w-xl mx-auto bg-[#FFE7A0]/10" data-testid="generating-progress-bar">
              <motion.div
                className="absolute top-0 left-0 h-[2px] bg-[#FFE7A0]"
                style={{ boxShadow: "0 0 12px rgba(255, 231, 160,0.7)" }}
                initial={false}
                animate={{ width: `${displayPct}%` }}
                transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              />
            </div>
            <div className="font-cinzel text-[0.6rem] tracking-[0.25em] uppercase text-[#F0E6D2]/50 mb-14">
              {Math.round(displayPct)}%
            </div>

            {/* Section ladder — tiny dots with names visible on hover/larger screens */}
            <div
              className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 max-w-2xl mx-auto text-left"
              data-testid="generating-section-list"
            >
              {SECTIONS.map((s, idx) => {
                const n = idx + 1;
                const state = displayDone
                  ? "done"
                  : displaySealing
                    ? "done"
                    : n < displayCurrent
                      ? "done"
                      : n === displayCurrent
                        ? "active"
                        : "pending";
                return (
                  <div
                    key={s}
                    className={`flex items-center gap-3 text-xs font-body font-light transition-all duration-500 ${
                      state === "done"
                        ? "text-[#FFE7A0]/85"
                        : state === "active"
                          ? "text-[#F0E6D2]"
                          : "text-[#F0E6D2]/30"
                    }`}
                    data-testid={`generating-step-${n}`}
                  >
                    <span
                      className={`inline-block flex-shrink-0 w-2 h-2 rounded-full transition-all duration-500 ${
                        state === "done"
                          ? "bg-[#FFE7A0]"
                          : state === "active"
                            ? "bg-[#FFE7A0] animate-pulse"
                            : "bg-[#F0E6D2]/20"
                      }`}
                      style={state !== "pending" ? { boxShadow: "0 0 10px rgba(255, 231, 160,0.6)" } : undefined}
                    />
                    <span className="font-cinzel text-[0.55rem] tracking-[0.22em] uppercase opacity-60 w-5 flex-shrink-0">
                      {String(n).padStart(2, "0")}
                    </span>
                    <span className="truncate">{s}</span>
                  </div>
                );
              })}
            </div>

            {/* Android-aware reassurance + manual rescue */}
            <p className="mt-10 font-cinzel text-[0.6rem] tracking-[0.25em] uppercase text-[#F0E6D2]/35 italic">
              Typically ~90 seconds · Keep this tab open · Screen will stay awake
            </p>

            {stuckSeconds > 3 && !readyPacketId && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="mt-10 border border-[#FFE7A0]/30 bg-[#FFE7A0]/5 p-6 rounded-sm max-w-md mx-auto"
                data-testid="generating-stuck-rescue"
              >
                <p className="text-sm text-[#F0E6D2]/75 font-body font-light mb-5 leading-relaxed">
                  Your packet is likely ready. Android browsers sometimes pause background updates —
                  tap below to open it now.
                </p>
                <button
                  type="button"
                  onClick={pokeJob}
                  className="rm-btn w-full"
                  data-testid="generating-poke-btn"
                >
                  Open my packet →
                </button>
              </motion.div>
            )}
          </motion.div>
        </div>
      )}

      {err && (
        <div className="relative z-10 min-h-screen flex items-center justify-center px-6">
          <div className="text-center max-w-md" data-testid="generating-error">
            <p className="font-serif-display italic text-3xl text-[#F0E6D2] mb-6">{err}</p>
            <button className="rm-btn" onClick={() => navigate("/intake")} data-testid="generating-retry-btn">
              Return to Intake
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
